import React from "react";
import { Tooltip, UnstyledButton } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import styles from "./InfoTooltip.module.css";

/** Wide enough to wrap a metric description + formula into a few lines. */
export const DEFAULT_TOOLTIP_WIDTH = 250;

export interface InfoTooltipProps {
  /** Tooltip body. Plain text or a rich node such as <MetricInfo />. */
  label: React.ReactNode;
  /** Accessible name for the trigger, e.g. "About Net sales". */
  ariaLabel: string;
  /** Icon size in px. Defaults to 14. */
  size?: number;
  /** Tooltip width in px. Defaults to DEFAULT_TOOLTIP_WIDTH. */
  width?: number;
  position?: "top" | "bottom" | "left" | "right";
}

/**
 * An info icon that reveals help text on hover, keyboard focus, or tap. The
 * trigger is a real (focusable) button so keyboard users can read the
 * tooltip, and it stops click propagation so it can sit inside a clickable
 * row without toggling it. Never nest it inside another button — place it as
 * a sibling of a header's sort button instead.
 */
export default function InfoTooltip({
  label,
  ariaLabel,
  size = 14,
  width = DEFAULT_TOOLTIP_WIDTH,
  position = "bottom",
}: InfoTooltipProps) {
  return (
    <Tooltip
      label={label}
      multiline
      w={width}
      withArrow
      position={position}
      events={{ hover: true, focus: true, touch: true }}
    >
      <UnstyledButton
        type="button"
        aria-label={ariaLabel}
        className={styles.trigger}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <IconInfoCircle size={size} aria-hidden />
      </UnstyledButton>
    </Tooltip>
  );
}
